import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Check, HelpCircle, Info } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SOIN_GENERAL, SOINS_CIBLES, SOIN_APPROFONDI, ACCOMPAGNEMENT_28, ACCOMPAGNEMENTS_28, ACCOMPAGNEMENTS_28_PRIX, ACCOMPAGNEMENT_28_LOGO, CONTACT_SOIN_URL } from "@/data/soins";

export const BtnNeSaisPas = ({ className = "", light = false, testid = "soins-ne-sais-pas" }) => (
  <Link to={CONTACT_SOIN_URL} data-testid={testid}
    className={`inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full border text-sm tracking-wide transition-colors duration-300 ${light ? "border-sand/40 text-sand hover:bg-sand hover:text-earth" : "border-terra text-bark hover:bg-terra hover:text-earth"} ${className}`}>
    <HelpCircle className="w-4 h-4" /> Je ne sais pas quel soin choisir
  </Link>
);

const Fiche = ({ s, prefix }) => (
  <dl className="space-y-3 text-sm" data-testid={`${prefix}-fiche`}>
    {[["Ce que c'est", s.methode], ["Comment cela se déroule", s.deroulement], ["Pour qui", s.pourQui]].map(([l, v]) => (
      <div key={l}><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee mb-1">{l}</dt><dd className="text-bark/85 leading-relaxed">{v}</dd></div>
    ))}
  </dl>
);

const Prix = ({ prix, suffix }) => <span className="font-serif text-xl text-terra">CHF {prix}.–{suffix && <span className="text-xs text-fumee font-sans"> {suffix}</span>}</span>;

const Porte = ({ lettre, titre, situation, children, delay = 0, testid }) => (
  <Reveal delay={delay} className="h-full">
    <article className="bg-card border border-sand-dark rounded-2xl p-7 lg:p-8 h-full flex flex-col" data-testid={testid}>
      <span className="font-serif text-5xl text-terra/80 leading-none">{lettre}</span>
      <h3 className="font-serif text-2xl text-bark mt-4 leading-tight">{titre}</h3>
      <p className="text-fumee leading-relaxed mt-3 text-sm">{situation}</p>
      <div className="mt-5 pt-5 border-t border-sand-dark/70 flex-1 flex flex-col">{children}</div>
    </article>
  </Reveal>
);

export const SoinsPortes = ({ onChoisir }) => (
  <section id="quel-soin" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 scroll-mt-24" data-testid="soins-quel-soin">
    <Reveal className="max-w-2xl mb-12">
      <p className="eyebrow mb-6">Quel soin choisir ?</p>
      <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight">Partez de ce que vous vivez. La méthode vient ensuite.</h2>
      <p className="text-fumee mt-4 leading-relaxed">Trois portes d'entrée, selon votre situation. Chacune indique ce que c'est, comment cela se déroule, pour qui et à quel tarif.</p>
    </Reveal>
    <div className="grid md:grid-cols-3 gap-6">
      <Porte lettre="A" titre="Retrouver de l'équilibre" situation={SOIN_GENERAL.situation} testid="porte-equilibre">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mb-4">→ {SOIN_GENERAL.titre}</p>
        <Fiche s={SOIN_GENERAL} prefix="porte-a" />
        <div className="mt-auto pt-6 flex items-center justify-between gap-3">
          <Prix prix={SOIN_GENERAL.prix} />
          <button type="button" onClick={() => onChoisir(SOIN_GENERAL)} data-testid="soin-choisir-general" className="inline-flex items-center gap-2 text-sm text-bark hover:text-terra transition-colors">Demander ce soin <ArrowRight className="w-4 h-4" /></button>
        </div>
      </Porte>
      <Porte lettre="B" titre="Traverser une situation particulière" delay={0.08} testid="porte-situation"
        situation="Une relation terminée qui pèse encore, une énergie qui ne se relance pas — ou une situation lourde qui dure et pour laquelle les approches habituelles semblent insuffisantes.">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mb-4">→ Soins ciblés</p>
        <ul className="space-y-2.5 text-sm text-bark/85">
          {SOINS_CIBLES.map((s) => <li key={s.id} className="flex justify-between gap-3"><span className="flex gap-2"><Check className="w-3.5 h-3.5 text-terra shrink-0 mt-1" />{s.titre}</span><span className="font-mono text-xs text-fumee shrink-0">CHF {s.prix}.–</span></li>)}
          <li className="flex justify-between gap-3 pt-2.5 border-t border-sand-dark/70"><span className="flex gap-2"><Check className="w-3.5 h-3.5 text-terra shrink-0 mt-1" /><span>{SOIN_APPROFONDI.titre} <span className="text-fumee">· option approfondie</span></span></span><span className="font-mono text-xs text-fumee shrink-0">CHF {SOIN_APPROFONDI.prix}.–</span></li>
        </ul>
        <p className="text-sm text-fumee leading-relaxed mt-4">Chaque soin cible une situation précise, à distance et sous 48 heures. L'option approfondie s'adresse aux situations lourdes ou complexes et commence par un échange.</p>
        <div className="mt-auto pt-6 flex items-center justify-between gap-3">
          <span className="font-serif text-xl text-terra">CHF 100.– à 300.–</span>
          <a href="#soins-cibles" data-testid="porte-b-voir-cibles" className="inline-flex items-center gap-2 text-sm text-bark hover:text-terra transition-colors">Voir les soins ciblés <ArrowDown className="w-4 h-4" /></a>
        </div>
      </Porte>
      <Porte lettre="C" titre="Être accompagné·e dans la durée" situation={ACCOMPAGNEMENT_28.situation} delay={0.16} testid="porte-duree">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mb-4">→ Accompagnement 28 jours</p>
        <Fiche s={ACCOMPAGNEMENT_28} prefix="porte-c" />
        <div className="mt-auto pt-6 flex items-center justify-between gap-3">
          <Prix prix={ACCOMPAGNEMENTS_28_PRIX} suffix="/ 28 jours" />
          <a href="#accompagnements-28" data-testid="porte-c-voir-28" className="inline-flex items-center gap-2 text-sm text-bark hover:text-terra transition-colors">Voir les deux formules <ArrowDown className="w-4 h-4" /></a>
        </div>
      </Porte>
    </div>
    <Reveal className="mt-10 flex justify-center"><BtnNeSaisPas testid="soins-ne-sais-pas-portes" /></Reveal>
  </section>
);

const CibleCard = ({ s, onChoisir, delay }) => (
  <Reveal delay={delay}>
    <article className="bg-card border border-sand-dark rounded-2xl overflow-hidden h-full flex flex-col" data-testid={`soin-formule-${s.id}`}>
      <div className="aspect-[16/7] overflow-hidden"><img src={s.image} alt={s.titre} loading="lazy" className="w-full h-full object-cover" /></div>
      <div className="p-6 flex flex-col flex-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee">{s.sous}</p>
        <h4 className="font-serif text-2xl text-bark mt-2 leading-tight">{s.titre}</h4>
        <p className="text-sm text-bark/85 leading-relaxed mt-3"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee block mb-1">Votre situation</span>{s.situation}</p>
        <div className="mt-4"><Fiche s={s} prefix={`cible-${s.id}`} /></div>
        {s.details && (
          <Accordion type="single" collapsible className="mt-3">
            <AccordionItem value="d" className="border-sand-dark/70" data-testid={`soin-details-${s.id}`}>
              <AccordionTrigger className="text-xs text-fumee hover:text-terra hover:no-underline py-3">En savoir plus sur cette lecture</AccordionTrigger>
              <AccordionContent className="text-sm text-bark/80 space-y-2">
                {s.details.map((d) => <p key={d}>{d}</p>)}
                <ul className="space-y-1.5">{s.points.map((p) => <li key={p} className="flex gap-2"><Check className="w-3.5 h-3.5 text-terra shrink-0 mt-1" />{p}</li>)}</ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
        <div className="flex items-center justify-between mt-auto pt-5 border-t border-sand-dark">
          <Prix prix={s.prix} />
          <button type="button" onClick={() => onChoisir(s)} data-testid={`soin-choisir-${s.id}`} className="inline-flex items-center gap-2 text-sm text-bark hover:text-terra transition-colors">Demander ce soin <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </article>
  </Reveal>
);

const Approfondi = ({ onChoisir }) => {
  const s = SOIN_APPROFONDI;
  return (
    <Reveal delay={0.1}>
      <article className="bg-earth grain text-sand rounded-2xl p-7 lg:p-10 relative overflow-hidden" data-testid="soin-formule-liberation">
        <div className="relative z-[2] grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra">{s.sous}</p>
            <h4 className="font-serif text-2xl sm:text-3xl mt-3 leading-tight" data-testid="soin-approfondi-titre">{s.titre}</h4>
            <p className="text-sand/75 leading-relaxed mt-4 text-sm"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand/50 block mb-1">Votre situation</span>{s.situation}</p>
            <div className="mt-6 pt-5 border-t border-sand/15 flex items-center justify-between gap-4">
              <span className="font-serif text-xl text-terra">CHF {s.prix}.–</span>
              <button type="button" onClick={() => onChoisir(s)} data-testid="soin-choisir-liberation" className="inline-flex items-center gap-2 text-sm text-sand hover:text-terra transition-colors">Demander un échange <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
          <dl className="lg:col-span-7 space-y-4 text-sm" data-testid="soin-approfondi-fiche">
            {[["Ce que c'est", s.methode], ["Comment cela se déroule", s.deroulement], ["Pour qui", s.pourQui], ["Pourquoi ce tarif", s.tarifPourquoi]].map(([l, v]) => (
              <div key={l}><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand/50 mb-1">{l}</dt><dd className="text-sand/85 leading-relaxed">{v}</dd></div>
            ))}
            <p className="flex gap-3 text-sand/70 leading-relaxed border border-sand/20 rounded-xl p-4 mt-2" data-testid="soin-approfondi-avertissement"><Info className="w-4 h-4 shrink-0 mt-0.5 text-terra" />{s.avertissement}</p>
          </dl>
        </div>
      </article>
    </Reveal>
  );
};

export const SoinsCibles = ({ onChoisir }) => (
  <section id="soins-cibles" className="bg-sand-dark/50 border-y border-sand-dark scroll-mt-24" data-testid="soins-cibles">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
      <Reveal className="max-w-2xl mb-10">
        <p className="eyebrow mb-6">Porte B · Les soins ciblés</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight">Deux soins ciblés, et une option approfondie pour les situations complexes.</h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {SOINS_CIBLES.map((s, i) => <CibleCard key={s.id} s={s} onChoisir={onChoisir} delay={i * 0.08} />)}
      </div>
      <Approfondi onChoisir={onChoisir} />
    </div>
  </section>
);

export const Soins28 = ({ onChoisir }) => (
  <section id="accompagnements-28" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 scroll-mt-24" data-testid="accompagnements-28">
    <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-8">
      <div className="max-w-2xl">
        <p className="eyebrow mb-6">Porte C · Accompagnements 28 jours</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight">Transmission d'énergie à distance, chaque jour pendant 28 jours.</h2>
      </div>
      <img src={ACCOMPAGNEMENT_28_LOGO} alt="Entre Les Mondes — accompagnements 28 jours" className="w-20 h-20 rounded-full object-cover border border-sand-dark" loading="lazy" />
    </Reveal>
    <Reveal className="mb-10 max-w-3xl"><Fiche s={ACCOMPAGNEMENT_28} prefix="acc-28" /><p className="text-sm text-fumee italic leading-relaxed mt-4" data-testid="accompagnement-28-remarque">{ACCOMPAGNEMENT_28.remarque}</p></Reveal>
    <div className="grid md:grid-cols-2 gap-6">
      {ACCOMPAGNEMENTS_28.map((a, i) => (
        <Reveal key={a.id} delay={i * 0.1}>
          <article className="bg-card border border-sand-dark rounded-2xl p-8 h-full flex flex-col" data-testid={`accompagnement-${a.id}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee">Transmission d'énergie à distance · 28 jours</p>
            <h3 className="font-serif text-2xl text-bark mt-3 leading-tight">{a.titre}</h3>
            <p className="text-fumee leading-relaxed mt-3 text-sm">{a.texte}</p>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-bark/85">{a.points.map((p) => <li key={p} className="flex gap-2"><Check className="w-4 h-4 text-terra shrink-0 mt-0.5" />{p}</li>)}</ul>
            <div className="mt-auto pt-6 flex items-center justify-between">
              <Prix prix={ACCOMPAGNEMENTS_28_PRIX} suffix="/ 28 jours" />
              <button type="button" onClick={() => onChoisir({ titre: `${a.titre} — 28 jours`, prix: ACCOMPAGNEMENTS_28_PRIX, id: a.id })} data-testid={`accompagnement-choisir-${a.id}`} className="inline-flex items-center gap-2 text-sm text-bark hover:text-terra transition-colors">Demander cet accompagnement <ArrowRight className="w-4 h-4" /></button>
            </div>
          </article>
        </Reveal>
      ))}
    </div>
  </section>
);
